import type { UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';
import React, {
  type Dispatch,
  type SetStateAction,
  useState,
  useEffect,
  useRef,
  type FC
} from 'react';
import { type TFunction, Trans, useTranslation } from 'react-i18next';
import { Toast } from '@deque/cauldron-react';

import {
  JiraFieldMappersKeys,
  type TemplateMappingSchemaV1,
  type TemplateMappingSchemaV2
} from '../../../utils/integrations-client/jira/fieldMapping/types';
import { IssueFieldSource } from '../../../utils/integrations-client/jira/fieldMapping/types';
import type {
  SupportedIntegrationProductSlugs,
  ProductSlugs
} from '../../../constants';
import type { IssueToIntegrationSend, HttpError } from '../../../api-client';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '../../../analyticsInstances';
import {
  maxUrlLength,
  urlRegExp
} from '../../../utils/integrations-client/jira/fieldMapping/UrlMapper';
import { maxShortTextLength } from '../../../utils/integrations-client/jira/fieldMapping/ShortTextMapper';
import { maxLabelLength } from '../../../utils/integrations-client/jira/fieldMapping/LabelsMapper';
import { getIntegrationProductInfoFromSlug } from '../../../utils/get-integration-product-name-from-slug';
import JiraFieldMapper from '../../../utils/integrations-client/jira/fieldMapping';
import useIssueTypeFields from '../../../../app/hooks/useIssueTypeFields';
import ChooseTemplate from './Steps/ChooseTemplate/ChooseTemplate';
import { useGlobalToast } from '../../../contexts/globalToast';
import { useEnterprises } from '../../../contexts/enterprises';
import StepperModal, { type Step } from '../../StepperModal';
import { useAuthContext } from '../../../contexts/auth';
import SetValues from './Steps/SetValues/SetValues';
import { sendIssue } from '../../../api-client';
import Link from '../../Link';
import styles from './ShareToIntegrationModal.css';
import {
  type DefaultValueType,
  type AllowedValue,
  type IssueTypeField
} from '../../../utils/integrations-client/jira/getIssueTypeFields';
import { AxeIssueMappingSchema } from '../../../utils/integrations-client/jira/fieldMapping/MappingSchema';
import {
  getDefaultValueAsString,
  getDefaultValueAsStringOrNumber
} from '../../../../app/components/integrations/IntegrationTemplateWizard/Steps/FieldMapping/FieldMapping';
import useTemplatesForConnections from '../../../hooks/useTemplatesForConnections';
import type { IntegrationConnection } from '../../../utils/integrations-client/jira/connections';
import useIntegrationProjectsFromTemplates from '../../../hooks/useIntegrationProjectsFromTemplates';

export interface MappedIntegrationField {
  integrationFieldName: string;
  axeFieldValue: string | number;
  sourceType: IssueFieldSource;
  rawAllowedValues?: AllowedValue[];
  key: string;
  required: boolean;
  mapperId: JiraFieldMappersKeys;
  type: string;
  custom?: string;
  items?: string;
  error?: string;
  allowedValues?: string[];
  defaultValue?: DefaultValueType;
}

export interface ShareToIntegrationModalProps {
  integrationProductSlug: SupportedIntegrationProductSlugs;
  setShow: Dispatch<SetStateAction<boolean>>;
  issues: UniversalIssueWithScreenshot[];
  testId: string;
  issuesProductSourceSlug: ProductSlugs;
  multiple: boolean;
  onIntegrationShare: () => void;
  connections: IntegrationConnection[];
}

function getMapperValueAsString(mapperValue: unknown): string {
  return mapperValue as string;
}

const excludeFromMapping = ['reporter', 'issuetype', 'description', 'project'];

const findFieldMapper = (fields: IssueTypeField[], fieldKey: string) =>
  Object.values(JiraFieldMapper).find(fieldMapper =>
    fieldMapper.canMapThisField(fields, fieldKey)
  );

const getMappedIntegrationField = (
  mappingSchema: TemplateMappingSchemaV2 | TemplateMappingSchemaV1,
  fields: IssueTypeField[],
  issue: UniversalIssueWithScreenshot
) => {
  const isV2 = AxeIssueMappingSchema.isV2(mappingSchema);
  const fieldMappings = isV2 ? mappingSchema.mapping : mappingSchema;

  /* istanbul ignore next */
  const mappedFields = fields?.reduce<MappedIntegrationField[]>(
    (result, field) => {
      if (
        excludeFromMapping.includes(field.name) ||
        excludeFromMapping.includes(field.key)
      ) {
        return result;
      }

      const mapper =
        (isV2 && JiraFieldMapper[mappingSchema.mapping[field.key]?.mapperId]) ||
        findFieldMapper(fields, field.key);

      // If the field is not mappable, skip it.
      if (!mapper) {
        return result;
      }

      /* istanbul ignore next */
      const axeIssueFieldKey = isV2
        ? mappingSchema.mapping[field.key]?.source
        : mappingSchema[field.key];

      let axeFieldValue: string | number = '';

      /* istanbul ignore next */
      let sourceType: IssueFieldSource = isV2
        ? mappingSchema.mapping[field.key]?.sourceType
        : IssueFieldSource.Axe;

      if (!sourceType) {
        sourceType = mapper.isEnum
          ? IssueFieldSource.FixedValue
          : IssueFieldSource.Axe;
      }

      try {
        if (sourceType === IssueFieldSource.Axe) {
          /* istanbul ignore next */
          if (field.key in fieldMappings) {
            axeFieldValue = getMapperValueAsString(
              mapper?.formatFieldValue(
                fields,
                field.key,
                issue[axeIssueFieldKey as keyof UniversalIssueWithScreenshot]
              )
            );
          }

          if (!axeFieldValue) {
            axeFieldValue = getDefaultValueAsStringOrNumber(field.defaultValue);
          }
        } else if (sourceType === IssueFieldSource.FixedValue) {
          axeFieldValue = axeIssueFieldKey;

          if (!axeFieldValue) {
            axeFieldValue = getDefaultValueAsString(field.defaultValue);
          }
        }
      } catch (error) {
        // If an error is thrown, the mapped value cannot be formatted to the type of the jira Field, so return an empty string.
        // Swallow the error.
      }

      /* istanbul ignore next */
      result.push({
        integrationFieldName: field.name,
        key: field.key,
        axeFieldValue,
        sourceType,
        rawAllowedValues: field.allowedValues,
        required: field.required,
        mapperId: mapper?.id ?? '',
        type: field.schema.type,
        items: field.schema.items,
        allowedValues: field.allowedValues?.map(v => v.value),
        custom: field.schema.custom
      });
      return result;
    },
    []
  );

  /* istanbul ignore next */
  return mappedFields ?? [];
};

type MappedValueValidator = Partial<
  Record<
    JiraFieldMappersKeys,
    (axeFieldValue: string, t: TFunction) => void | string
  >
>;

const mappedValueValidator: MappedValueValidator = {
  [JiraFieldMappersKeys.Labels]: (axeFieldValue, t) => {
    if (axeFieldValue.includes(' ')) {
      return t('Fields of type "label" cannot contain spaces');
    }

    if (axeFieldValue.length > maxLabelLength) {
      return t(
        'Fields of type "label" cannot be longer than {{ maxLabelLength }} characters',
        { maxLabelLength }
      );
    }
  },
  [JiraFieldMappersKeys.Url]: (axeFieldValue, t) => {
    if (axeFieldValue.length > maxUrlLength) {
      return t(
        'Fields of type "URL" cannot be longer than {{ maxUrlLength }} characters',
        { maxUrlLength }
      );
    }

    if (axeFieldValue && !urlRegExp.test(axeFieldValue)) {
      return t('Fields of type "URL" must be a valid URL');
    }
  },
  [JiraFieldMappersKeys.ShortText]: (axeFieldValue, t) => {
    if (axeFieldValue.length > maxShortTextLength) {
      return t(
        'Fields of type "short text" cannot be longer than {{ maxShortTextLength }} characters',
        { maxShortTextLength }
      );
    }
  }
};

type NoTemplatesToastProps = {
  show?: boolean;
  capitalizedIntegrationName: string;
};
const NoTemplatesToast: FC<NoTemplatesToastProps> = ({
  show = false,
  capitalizedIntegrationName
}) =>
  show ? (
    <Toast
      show={show}
      dismissible={false}
      type="error"
      className={styles.chooseTemplateErrorToast}
    >
      <Trans>
        No templates available for{' '}
        {{
          capitalizedIntegrationName
        }}
        . Please{' '}
        <Link
          url={`/configuration/integrations`}
          className={styles.errorToastLink}
        >
          configure a template
        </Link>{' '}
        before sharing issue to{' '}
        {{
          capitalizedIntegrationName
        }}
        .
      </Trans>
    </Toast>
  ) : null;

const ShareToIntegrationModal = ({
  connections,
  integrationProductSlug,
  setShow,
  issues,
  testId,
  issuesProductSourceSlug,
  onIntegrationShare,
  multiple
}: ShareToIntegrationModalProps) => {
  const fieldRefs = useRef<
    (HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)[]
  >([]);
  const { t } = useTranslation();
  const analytics =
    analyticsInstances[getFallbackAnalyticsInstanceId(integrationProductSlug)];

  const { setContents } = useGlobalToast();
  const { user } = useAuthContext();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  const { activeEnterprise } = useEnterprises();

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateComboboxError, setTemplateComboboxError] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [integrationFields, setIntegrationFields] = useState<
    MappedIntegrationField[]
  >([]);
  const [sendIssueError, setSendIssueError] = useState('');
  const { templates, templatesError, isTemplatesLoading } =
    useTemplatesForConnections({
      connections,
      integrationProductSlug
    });

  const selectedTemplateEntity = templates?.find(
    template => template.id === selectedTemplate
  );

  const { projectsByConnections, isProjectsLoading, projectsError } =
    useIntegrationProjectsFromTemplates({ connections, templates });

  const {
    data: fields,
    loading: fieldsLoading,
    error: fieldsError
  } = useIssueTypeFields(
    activeEnterprise?.id ?? '',
    selectedTemplateEntity?.project_id ?? '',
    selectedTemplateEntity?.issue_type_id ?? '',
    selectedTemplateEntity?.integration_connection_id ?? '',
    token,
    !selectedTemplateEntity
  );

  const loading = isTemplatesLoading || isProjectsLoading;

  const capitalizedIntegrationName = getIntegrationProductInfoFromSlug(
    integrationProductSlug as unknown as ProductSlugs
  ).displayName;

  /* istanbul ignore next */
  const selectedConnectionId =
    selectedTemplateEntity?.integration_connection_id || '';

  const projectEntity = projectsByConnections[selectedConnectionId]?.find(
    project => project.id === selectedTemplateEntity?.project_id
  );
  const selectedProjectName = projectEntity?.name || '';
  const issueTypeEntity = projectEntity?.issueTypes?.find(
    issueType => issueType.id === selectedTemplateEntity?.issue_type_id
  );
  const selectedIssueTypeName = issueTypeEntity?.name || '';

  const selectedTemplateName = selectedTemplateEntity?.name || '';

  useEffect(() => {
    if (!fields || !selectedTemplateEntity) {
      return;
    }

    /* istanbul ignore next */
    const mappedFields = getMappedIntegrationField(
      selectedTemplateEntity?.field_mappings || {},
      fields,
      issues[0]
    ).sort((a, b) => Number(b.required) - Number(a.required));

    setIntegrationFields(mappedFields);
  }, [fields, selectedTemplateEntity]);

  const analyticsData = {
    integration: integrationProductSlug,
    project: selectedProjectName,
    issueType: selectedIssueTypeName,
    template: selectedTemplateName,
    multiple
  };

  //TODO: add fingerprint handling
  const issuesToSend: IssueToIntegrationSend[] = issues
    .map((issue, index) => {
      const issueId =
        issue.issueId ||
        [
          selectedTemplateEntity?.project_id,
          issue.pageStateKey,
          issue.ruleId,
          index
        ].join('_');

      return { ...issue, issueId };
    })
    .filter((issue): issue is IssueToIntegrationSend => !!issue.shareURL);

  const onShare = async () => {
    const mappedIntegrationFields = integrationFields.map(field => ({
      key: field.key,
      value: field.axeFieldValue
        ? JiraFieldMapper[field.mapperId].formatFieldValue(
            fields ?? [],
            field.key,
            field.axeFieldValue
          )
        : undefined
    }));

    analytics.integrationIssueSendClick(analyticsData);

    setSendIssueError('');
    setContents(null);
    setShareLoading(true);
    try {
      await sendIssue(
        token,
        activeEnterprise?.id || '',
        selectedTemplateEntity?.integration_connection_id || '',
        {
          issues: issuesToSend,
          group_id: testId,
          product_source_slug: issuesProductSourceSlug,
          template_id: selectedTemplate,
          mappedIntegrationFields
        }
      );
      analytics.integrationIssueSendSuccess(analyticsData);

      setContents(
        t(`{{capitalizedIntegrationName}} {{issue}} sent`, {
          issue: multiple ? 'issues' : 'issue',
          capitalizedIntegrationName
        }),
        'confirmation'
      );

      /* istanbul ignore next */
      onIntegrationShare?.();
      setShow(false);
    } catch (err) {
      const e = err as HttpError;
      analytics.integrationIssueSendFail(analyticsData);
      // eslint-disable-next-line no-console
      console.error('Error returned from Jira', e.details);

      const errorMessage = multiple
        ? t(
            'Error with sending all issues to {{ capitalizedIntegrationName }}.',
            {
              capitalizedIntegrationName
            }
          )
        : t('Error with sending issue to {{ capitalizedIntegrationName }}.', {
            capitalizedIntegrationName
          });
      const errorDetails = e.details?.length
        ? t('The following fields are invalid: {{ errorFields }}', {
            errorFields: e.details.join(', ')
          })
        : '';
      setSendIssueError(`${errorMessage} ${errorDetails}`);
    } finally {
      setShareLoading(false);
    }
  };

  const focusOnError = (index: number) => {
    const fieldRef = fieldRefs.current[index];
    if (fieldRef) {
      fieldRef.focus();
    }
  };

  const steps: Step[] = [
    {
      heading: t('Choose template'),
      content: (
        <ChooseTemplate
          loading={loading}
          loadingError={!!templatesError || !!projectsError}
          templateError={templateComboboxError}
          capitalizedIntegrationName={capitalizedIntegrationName}
          availableTemplates={templates}
          connectedIntegrations={connections}
          multiple={multiple}
          projectsByConnections={projectsByConnections}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
        />
      ),
      isValid: () => {
        if (!selectedTemplate) {
          setTemplateComboboxError(t('Template is required'));
        }

        return !!selectedTemplate && !!projectEntity && !!issueTypeEntity;
      }
    },
    {
      heading: t('Set values'),
      content: (
        <SetValues
          capitalizedIntegrationName={capitalizedIntegrationName}
          integrationFields={integrationFields}
          setIntegrationFields={setIntegrationFields}
          loading={loading || fieldsLoading}
          error={fieldsError}
          fieldRefs={fieldRefs}
        />
      ),
      isValid: () => {
        let hasError = false;

        const updatedFields = integrationFields.map(field => {
          const { required, mapperId, integrationFieldName, axeFieldValue } =
            field;
          const validationError =
            typeof axeFieldValue === 'string'
              ? mappedValueValidator[mapperId]?.(axeFieldValue, t)
              : null;

          if (validationError) {
            hasError = true;
            return { ...field, error: validationError };
          }

          if (required && !axeFieldValue) {
            hasError = true;
            const fieldName = integrationFieldName
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            return {
              ...field,
              error: t(
                `{{fieldName}} {{capitalizedIntegrationName}} Field is required`,
                { fieldName, capitalizedIntegrationName }
              )
            };
          }

          return { ...field, error: undefined };
        });

        setIntegrationFields(updatedFields);

        if (hasError) {
          focusOnError(updatedFields.findIndex(f => f.error)); // Place focus on the first error
        }

        return !hasError;
      }
    }
  ];

  // check if project or issue type wasn't deleted in Jira
  const hasProjectAndIssueType =
    selectedTemplateEntity && (!projectEntity || !issueTypeEntity);

  return (
    <StepperModal
      heading={t('Share {{ multiple }}to {{ capitalizedIntegrationName }}', {
        multiple: multiple ? t('All ') : '',
        capitalizedIntegrationName
      })}
      onSubmit={onShare}
      onClose={() => setShow(false)}
      dialogOpen
      steps={steps}
      isLoading={shareLoading}
      toast={
        <>
          <NoTemplatesToast
            show={!loading && !templates?.length}
            capitalizedIntegrationName={capitalizedIntegrationName}
          />

          {!!sendIssueError && (
            <Toast
              show
              onDismiss={() => setSendIssueError('')}
              type="error"
              className={styles.chooseTemplateErrorToast}
            >
              {sendIssueError}
            </Toast>
          )}

          {hasProjectAndIssueType && (
            <Toast
              show
              dismissible
              type="error"
              className={styles.chooseTemplateErrorToast}
            >
              {t(
                'Project or issue type selected in the template was not found'
              )}
            </Toast>
          )}
        </>
      }
      nextDisabled={
        loading || shareLoading || !templates.length || hasProjectAndIssueType
      }
    />
  );
};

export default ShareToIntegrationModal;
